import { useCallback, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  OptroControlSummary,
  OptroWorkpaperFile,
} from "../../../../models/audit/optroSync";
import {
  OptroImportWizardContext,
  OptroImportWizardValue,
} from "./optroImportWizardContext";
import { createLinkedWorkpapersCache } from "../../../../hooks/useOptroLinkedWorkpapers";

export const OptroImportWizardLayout = () => {
  const [selected, setSelected] = useState<OptroControlSummary[]>([]);
  const [workpaperDecisions, setWorkpaperDecisions] = useState<
    Record<string, OptroWorkpaperFile[]>
  >({});

  const toggleControl = useCallback((control: OptroControlSummary) => {
    setSelected((prev) => {
      const isAlreadySelected = prev.some(
        (c) => c.externalRef === control.externalRef,
      );

      if (isAlreadySelected) {
        return prev.filter((c) => c.externalRef !== control.externalRef);
      }

      return [...prev, control];
    });
  }, []);

  const isSelected = useCallback(
    (externalRef: string) =>
      selected.some((c) => c.externalRef === externalRef),
    [selected],
  );

  // Bulk-add for select-all: merges by externalRef, keeping any existing
  // selections (and their order) intact.
  const selectMany = useCallback((controls: OptroControlSummary[]) => {
    setSelected((prev) => {
      const existing = new Set(prev.map((c) => c.externalRef));
      const additions = controls.filter((c) => !existing.has(c.externalRef));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  const [userEditedRefs, setUserEditedRefs] = useState<Set<string>>(
    () => new Set(),
  );

  // One per wizard instance; mutated in place by the hook, never replaced.
  const [linkedWorkpapersCache] = useState(createLinkedWorkpapersCache);

  const clearSelection = useCallback(() => {
    setSelected([]);
    setWorkpaperDecisions({});
    setUserEditedRefs(new Set());
  }, []);

  const setWorkpapers = useCallback(
    (
      externalRef: string,
      files: OptroWorkpaperFile[],
      source: "match" | "user",
    ) => {
      setWorkpaperDecisions((prev) => ({ ...prev, [externalRef]: files }));
      // Only a user decision is recorded. The match job seeds through this
      // same function, and treating that as an edit would defeat the point:
      // every ref would look user-decided the moment its result landed.
      if (source === "user") {
        setUserEditedRefs((prev) => {
          if (prev.has(externalRef)) return prev;
          const next = new Set(prev);
          next.add(externalRef);
          return next;
        });
      }
    },
    [],
  );

  const value = useMemo<OptroImportWizardValue>(
    () => ({
      selected,
      toggleControl,
      isSelected,
      selectMany,
      clearSelection,
      workpaperDecisions,
      setWorkpapers,
      userEditedRefs,
      linkedWorkpapersCache,
    }),
    [
      selected,
      toggleControl,
      isSelected,
      selectMany,
      clearSelection,
      workpaperDecisions,
      setWorkpapers,
      userEditedRefs,
      linkedWorkpapersCache,
    ],
  );

  return (
    <OptroImportWizardContext.Provider value={value}>
      <Outlet />
    </OptroImportWizardContext.Provider>
  );
};
